import { useEffect, useMemo, useState } from 'react';
import { Sparkles } from 'lucide-react';
import type { CreateTaskInput, Task, TaskDraft, TaskPriority } from '../../lib/tasksApi';
import { PRIORITY_OPTIONS } from './taskConstants';
import { useDepartments } from '../../hooks/useDepartments';
import { useServices } from '../../hooks/useServices';
import { useProjects } from '../../hooks/useProjects';
import { useGeneralProject } from '../../hooks/useTasks';
import { useTaskStatuses } from '../../hooks/useTaskStatuses';
import { usePeople } from '../../hooks/usePeople';
import { useAuthStore } from '../../store/useAuthStore';
import { LabelSelect } from './LabelSelect';
import { ProjectSelect } from './ProjectSelect';
import { Select } from '../ui/Select';
import { DateField } from '../ui/DateField';
import { parseEstimate, formatEstimate } from '../../lib/estimate';

interface TaskFormProps {
  initialData?: Task;
  onSubmit: (input: CreateTaskInput) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
  // When the form is opened in a project context (e.g. a per-project board)
  // pass the project to preselect it. Without one the task goes to the
  // company's "Fără proiect" container (tasks without a project).
  defaultProjectId?: string;
  // Lock the picker when the project must not change here (e.g. a project's own
  // Tasks tab) — the field renders read-only but still submits the projectId.
  lockProject?: boolean;
  // AI draft-in-form: pre-fills title/description/priority/dueDate/estimate on
  // create. Only read by the useState initializers — the parent must remount
  // the form (key={draftKey}) when a new draft arrives.
  initialDraft?: TaskDraft;
}

function toDateInput(value: string | null | undefined): string {
  if (!value) return '';
  return new Date(value).toISOString().slice(0, 10);
}

/**
 * Controlled task form for create and edit; the backend re-validates with zod.
 *
 * Create (no `initialData`) asks only for a description: there is no title
 * field and no service field. The server sends the description to Haiku 5.5,
 * which writes the title and tidies the text. Edit keeps the title and the
 * service so existing tasks stay fully editable.
 */
export function TaskForm({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting,
  submitLabel = 'Salvează',
  defaultProjectId,
  lockProject = false,
  initialDraft,
}: TaskFormProps) {
  const [title, setTitle] = useState(initialData?.title ?? initialDraft?.title ?? '');
  const [description, setDescription] = useState(
    initialData?.description ?? initialDraft?.description ?? '',
  );
  const [projectId, setProjectId] = useState<string>(
    initialData?.projectId ?? defaultProjectId ?? '',
  );
  const [statusId, setStatusId] = useState(initialData?.statusId ?? '');
  const [priority, setPriority] = useState<TaskPriority>(
    initialData?.priority ?? initialDraft?.priority ?? 'NONE',
  );
  const [departmentId, setDepartmentId] = useState(initialData?.departmentId ?? '');
  const [serviceId, setServiceId] = useState(initialData?.serviceId ?? '');
  const [dueDate, setDueDate] = useState(
    toDateInput(initialData?.dueDate ?? initialDraft?.dueDate),
  );
  // CLE-435: estimate is entered as a duration string ("1h 30m", "30m", "2h").
  // Seed it from the stored decimal hours by formatting it back to that form.
  const [estimate, setEstimate] = useState(
    initialData?.estimate != null
      ? formatEstimate(initialData.estimate, '')
      : initialDraft?.estimate != null
        ? formatEstimate(initialDraft.estimate, '')
        : '',
  );
  // Create only: who the task goes to. Defaults to the current user; editing
  // reassigns through the inline assignee editor instead.
  const callerId = useAuthStore((s) => s.user?.id ?? '');
  const [assigneeId, setAssigneeId] = useState(callerId);
  const [labelIds, setLabelIds] = useState<string[]>(initialData?.labels.map((l) => l.id) ?? []);
  const [error, setError] = useState<string | null>(null);
  const isEdit = Boolean(initialData);

  const { data: departments } = useDepartments();
  const { data: people } = usePeople({ active: 'true' });
  // Only people linked to a User account can be assignees (the FK points at User).
  const assigneeOptions = useMemo(
    () =>
      (people ?? [])
        .filter((p) => p.userId)
        .map((p) => ({ value: p.userId as string, label: p.fullName })),
    [people],
  );
  const { data: services } = useServices();
  // Configurable statuses (CLE-DASH): options come from the DB; new tasks
  // default to the configured default status once statuses load.
  const { statuses, defaultStatus, isLoading: statusesLoading } = useTaskStatuses();
  useEffect(() => {
    if (!statusId && defaultStatus) setStatusId(defaultStatus.id);
  }, [statusId, defaultStatus]);
  const { data: projectsData } = useProjects({ limit: 200 });
  const projects = projectsData?.projects ?? [];
  // "Fără proiect" is the default pick on create: the task lands in the
  // company's general container and shows up in Sarcini / Calendar / the
  // colleague's tab like any other task.
  const { data: generalProject } = useGeneralProject();
  useEffect(() => {
    if (!initialData && !projectId && generalProject) setProjectId(generalProject.id);
  }, [initialData, projectId, generalProject]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEdit && !title.trim()) {
      setError('Titlul este obligatoriu');
      return;
    }
    if (!isEdit && !description.trim()) {
      setError('Scrie ce trebuie făcut în descriere.');
      return;
    }
    const chosenProjectId = projectId || generalProject?.id || '';
    if (!chosenProjectId) {
      setError('Alege un proiect sau așteaptă să se încarce „Fără proiect”.');
      return;
    }
    const parsedEstimate = parseEstimate(estimate);
    if (parsedEstimate.error) {
      setError('Estimarea trebuie să fie o durată validă (ex: 1h 30m, 30m, 2h)');
      return;
    }
    const estimateNum = parsedEstimate.hours;
    setError(null);
    onSubmit({
      // Create: no title — the server writes it from the description.
      title: isEdit ? title.trim() : undefined,
      description: description.trim() || undefined,
      projectId: chosenProjectId,
      ...(isEdit ? {} : { assigneeId: assigneeId || null }),
      statusId: statusId || undefined,
      priority,
      // On create an omitted department lets the backend default it from the
      // assignee; on edit an explicit null clears it.
      departmentId: departmentId || (initialData ? null : undefined),
      // The service tag is always explicit — no backend default derived from
      // the assignee. Empty selection clears it on edit, omits it on create.
      serviceId: isEdit ? serviceId || null : undefined,
      dueDate: dueDate ? new Date(dueDate).toISOString() : null,
      estimate: estimateNum,
      labelIds,
    });
  };

  const selectedProjectName =
    initialData?.projectName ??
    projects.find((p) => p.id === projectId)?.name ??
    (generalProject && projectId === generalProject.id ? generalProject.name : '');

  return (
    <form onSubmit={handleSubmit} className="relative flex flex-col gap-3">
      {!isEdit && isSubmitting && (
        <div
          role="status"
          aria-live="polite"
          className="absolute -inset-2 z-10 flex flex-col items-center justify-center gap-4 rounded-xl bg-white/90 px-6 text-center backdrop-blur-sm"
        >
          <div className="relative flex h-14 w-14 items-center justify-center">
            <span className="absolute inset-0 animate-ping rounded-full bg-purple-500/20" />
            <span className="absolute inset-0 animate-spin rounded-full border-2 border-purple-500/30 border-t-purple-500" />
            <Sparkles size={22} className="animate-pulse text-purple-500" />
          </div>
          <p className="max-w-xs text-sm font-medium text-gray-700">
            AI-ul tocmai analizează datele tale și va crea sarcina în curând.
          </p>
        </div>
      )}
      {isEdit && (
        <label className="flex flex-col text-sm font-medium text-gray-700">
          Titlu
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="ui-input mt-1"
            required
          />
        </label>
      )}

      <label className="flex flex-col text-sm font-medium text-gray-700">
        Proiect
        {lockProject ? (
          <input value={selectedProjectName} readOnly className="ui-input mt-1 bg-gray-50" />
        ) : (
          <ProjectSelect value={projectId} onChange={setProjectId} />
        )}
      </label>

      <label className="flex flex-col text-sm font-medium text-gray-700">
        Descriere
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={isEdit ? 4 : 6}
          autoFocus={!isEdit}
          placeholder={
            isEdit
              ? undefined
              : 'Descrie ce trebuie făcut. Titlul se pune automat, iar textul e aranjat de AI la salvare.'
          }
          className="ui-textarea mt-1"
        />
      </label>

      <div className="flex gap-3">
        <Select
          label="Status"
          value={statusId}
          onChange={setStatusId}
          loading={statusesLoading}
          options={statuses.map((s) => ({ value: s.id, label: s.name, color: s.color }))}
          className="flex-1"
        />

        <Select
          label="Prioritate"
          value={priority}
          onChange={(v) => setPriority(v as TaskPriority)}
          options={PRIORITY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          className="flex-1"
        />

        <Select
          label="Departament"
          clearable
          value={departmentId}
          onChange={setDepartmentId}
          placeholder="—"
          options={(departments ?? []).map((d) => ({
            value: d.id,
            label: d.name,
            color: d.color,
          }))}
          className="flex-1"
        />
      </div>

      {!isEdit && (
        <Select
          label="Atribuit lui"
          clearable
          searchable
          value={assigneeId}
          onChange={setAssigneeId}
          placeholder="Neatribuit"
          options={assigneeOptions}
        />
      )}

      <div className="flex gap-3">
        {isEdit && (
          <Select
            label="Serviciu"
            clearable
            value={serviceId}
            onChange={setServiceId}
            placeholder="—"
            options={(services ?? []).map((s) => ({
              value: s.id,
              label: s.name,
              color: s.color ?? undefined,
            }))}
            className="flex-1"
          />
        )}

        <label className="flex flex-1 flex-col text-sm font-medium text-gray-700">
          Termen
          <DateField value={dueDate} onChange={setDueDate} className="mt-1 w-full" />
        </label>

        <label className="flex flex-1 flex-col text-sm font-medium text-gray-700">
          Estimare
          <input
            type="text"
            value={estimate}
            onChange={(e) => setEstimate(e.target.value)}
            placeholder="ex: 1h 30m"
            className="ui-input mt-1"
          />
        </label>
      </div>

      <LabelSelect label="Etichete" value={labelIds} onChange={setLabelIds} />

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="mt-2 flex justify-end gap-2">
        {onCancel && (
          <button type="button" onClick={onCancel} className="ui-btn-secondary">
            Anulează
          </button>
        )}
        <button type="submit" disabled={isSubmitting} className="ui-btn-primary">
          {isSubmitting ? (isEdit ? 'Se salvează…' : 'Se creează…') : submitLabel}
        </button>
      </div>
    </form>
  );
}
